import axios from 'axios'
import { msalInstance } from '@/auth/msalInstance'
import { apiTokenRequest, msalConfigured } from '@/auth/msalConfig'

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null
  return null
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // Without this, a hung backend call (or a hung MSAL silent-token acquisition
  // below, which axios's own timeout can't cover since it runs before the
  // request is dispatched) leaves callers like useProducts()/useUserRoles()
  // stuck in "loading" forever instead of settling into an error state.
  timeout: 20000,
})

// Attach Entra ID access token (Bearer) acquired silently from MSAL.
api.interceptors.request.use(async (config) => {
  if (msalConfigured) {
    try {
      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
      if (account) {
        // acquireTokenSilent can hang well past axios's own `timeout` (that only
        // covers the actual HTTP request, which hasn't started yet) — e.g. after
        // a stale MSAL cache entry or an Entra-side account/permission change.
        // Race it so a hang here can't block every request in the app forever.
        const result = await Promise.race([
          msalInstance.acquireTokenSilent({ ...apiTokenRequest, account }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('acquireTokenSilent timed out')), 10000),
          ),
        ])
        if (result.accessToken) {
          config.headers.Authorization = `Bearer ${result.accessToken}`
        }
      }
    } catch {
      // Silent acquisition can fail or time out (e.g. expired/stale session);
      // leave unauthenticated and let the 401 interceptor below redirect to /login.
    }
  }

  // Sent only when the actor switch is baked into this build. The API honors it for DevAutoAuthentication,
  // and on the shared dev site only for the two synthetic actors after a real sign-in.
  if (import.meta.env.VITE_DEV_ACTOR_SWITCH === 'true') {
    try {
      const actor = sessionStorage.getItem('ssp-dev-actor-email')
      if (actor) config.headers['X-Dev-User-Email'] = actor
    } catch {
      // sessionStorage can throw in a locked-down browser. The request continues as the default dev user.
    }
  }

  // Send CSRF token on mutating requests (for endpoints still using session auth).
  const method = config.method?.toLowerCase() ?? ''
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = getCookie('csrftoken')
    if (csrf) config.headers['X-CSRFToken'] = csrf
  }
  return config
})

// On 401/403 from auth-required endpoints, redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const onLoginPage = window.location.pathname === '/login'
    const isAuthError = status === 401 || status === 403
    // Only redirect if calling a non-auth endpoint (avoid loop on /auth/me/ failures)
    const isAuthEndpoint = (error.config?.url ?? '').includes('/auth/')
    if (isAuthError && !onLoginPage && !isAuthEndpoint) {
      const from = window.location.pathname + window.location.search
      window.location.href = `/login?from=${encodeURIComponent(from)}`
    }
    return Promise.reject(error)
  },
)
